import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Container from './Container';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { scrollToSection } from '../../utils/scrollUtils';

interface NavbarProps {
  transparent?: boolean;
}

const CyberdefenseNavbar = ({ transparent = false }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleBlogsClick = () => {
    scrollToSection('ai-intelligence');
  };

  const handleFeaturesClick = () => {
    scrollToSection('features');
  };

  const handleAboutClick = () => {
    scrollToSection('about');
  };

  const handleHomeClick = () => {
    navigate('/');
    // Ensure we scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  return (
    <NavbarContainer scrolled={scrolled} transparent={transparent}>
      <Container>
        <NavbarContent>
          <LogoContainer href="/">
            <LogoImage src="/images/cyberpage_logo.png" alt="Christopher Anto" />
            <Logo>Christopher Anto</Logo>
          </LogoContainer>

          <NavItems>
            <NavItem onClick={handleHomeClick}>Home</NavItem>
            <NavItem onClick={handleBlogsClick}>Blogs</NavItem>
            <NavItem onClick={handleFeaturesClick}>Features</NavItem>
            <NavItem onClick={handleAboutClick}>About</NavItem>
          </NavItems>

          <MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
            <HamburgerIcon isOpen={isOpen} />
          </MobileMenuButton>
        </NavbarContent>
      </Container>

      {isOpen && (
        <MobileMenu>
          <MobileNavItems>
            <MobileNavItem onClick={() => {
              setIsOpen(false);
              handleHomeClick();
            }}>
              Home
            </MobileNavItem>
            <MobileNavItem onClick={() => {
              setIsOpen(false);
              handleBlogsClick();
            }}>
              Blogs
            </MobileNavItem>
            <MobileNavItem onClick={() => {
              setIsOpen(false);
              handleFeaturesClick();
            }}>
              Features
            </MobileNavItem>
            <MobileNavItem onClick={() => {
              setIsOpen(false);
              handleAboutClick();
            }}>
              About
            </MobileNavItem>
          </MobileNavItems>
        </MobileMenu>
      )}
    </NavbarContainer>
  );
};

interface NavbarContainerProps {
  scrolled: boolean;
  transparent: boolean;
}

const NavbarContainer = styled.header<NavbarContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background-color: ${props => props.transparent && !props.scrolled
    ? 'transparent'
    : props.theme.colors.background
  };
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'};
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 70px;
  }
`;

const LogoContainer = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: color 0.3s ease;
  
  &:hover {
    color: #c084fc;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  margin-right: 10px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 30px;
  }
`;

const Logo = styled.span`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: bold;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.md};
  }
`;

const NavItems = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavItem = styled.div`
  margin-left: 2rem;
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: 500;
  opacity: 0.8;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    color: #c084fc;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
`;

interface HamburgerIconProps {
  isOpen: boolean;
}

const HamburgerIcon = styled.div<HamburgerIconProps>`
  width: 24px;
  height: 18px;
  position: relative;
  transform: rotate(0deg);
  transition: 0.5s ease-in-out;
  cursor: pointer;

  &:before,
  &:after {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${props => props.theme.colors.text};
    border-radius: 9px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: 0.25s ease-in-out;
  }

  &:before {
    content: '';
    top: ${props => (props.isOpen ? '8px' : '0')};
    transform: ${props => (props.isOpen ? 'rotate(135deg)' : 'none')};
  }

  &:after {
    content: '';
    top: ${props => (props.isOpen ? '8px' : '16px')};
    transform: ${props => (props.isOpen ? 'rotate(-135deg)' : 'none')};
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  height: auto;
  background-color: ${props => props.theme.colors.background};
  box-shadow: ${props => props.theme.boxShadow.normal};
  z-index: 200;
  padding: 0;
  backdrop-filter: blur(10px);
`;

const MobileNavItems = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0 1rem;
`;

const MobileNavItem = styled.div`
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  transition: color 0.3s ease;

  &:hover {
    color: #c084fc;
  }
`;

export default CyberdefenseNavbar;
